import type { Menu, Reference } from '@ps/web-martech-cms-types';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { useCMSReference } from '@swf/cms';
import useTouchSupported from '@swf/core/hooks/useTouchSupported';
import { select } from '@swf/core/store';

import CMSNavigationSubmenuChildren from '../CMSNavigationSubmenuChildren/CMSNavigationSubmenuChildren';
import CMSNavigationSubmenuItem from '../CMSNavigationSubmenuItem/CMSNavigationSubmenuItem';

import styles from './CMSNavigationSubmenu.scss';

interface Props {
  menuItemRef: Reference;
  onSelect: () => void;
}

const CMSNavigationSubmenu: React.FC<Props> = ({ menuItemRef, onSelect }) => {
  const content = useSelector(select.cms.getContent);
  const {
    children,
    page,
    heading,
    url,
    resolver_tag: resolverTag
  } = useCMSReference<Menu>(menuItemRef) as Menu;
  const getSubmenuData = (children: Reference[]) =>
    children.map((item) => content[item.uid] as Menu).filter((item) => item);
  const isTouchSupported = useTouchSupported();

  return (
    !!children.length && (
      <ul className={styles.menu} onMouseLeave={onSelect}>
        {!!isTouchSupported && (
          <li>
            <CMSNavigationSubmenuItem
              pageRef={page}
              heading={heading}
              url={url}
              resolverTag={resolverTag}
              closeMenu={isTouchSupported}
              className={classNames(styles.menuItem, styles.topMenuItem)}
              onSelect={onSelect}
            />
          </li>
        )}
        {getSubmenuData(children).map(
          ({ heading, children, page, uid, url, resolver_tag: resolverTag }) => (
            <li key={uid}>
              <CMSNavigationSubmenuItem
                pageRef={page}
                heading={heading}
                url={url}
                resolverTag={resolverTag}
                className={styles.menuItem}
                onSelect={onSelect}
              />
              {children.length > 0 && (
                <CMSNavigationSubmenuChildren
                  menuItems={getSubmenuData(children)}
                  parentPageRef={page}
                  parentUrl={url}
                  onSelect={onSelect}
                />
              )}
            </li>
          )
        )}
      </ul>
    )
  );
};

export default CMSNavigationSubmenu;
